import React from 'react';

var BONUS_LABELS = {
  joiningBonus: 'Joining Bonus',
  retentionBonus: 'Retention Bonus',
  referralBonus: 'Referral Bonus',
  specialAllowance: 'Special Allowance',
};

var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonth(monthStr) {
  if (!monthStr) return '';
  var parts = monthStr.split('-');
  return MONTHS[parseInt(parts[1], 10) - 1] + ' ' + parts[0];
}

// Component groups by ID
var REVIEWS_IDS = [1, 2, 3, 4];
var PATIENT_IDS = [5, 6];
var CHECKLIST_ID = 7;
var OTT_ID = 8;
var OVERTIME_IDS = [9, 10];
var ABSENT_ID = 11;

function CompRow(props) {
  var comp = props.comp;
  var compEntry = props.compEntry;
  var backendValue = props.backendValue;
  var isReadOnly = props.isReadOnly;
  var isCurrentMonth = props.isCurrentMonth;
  var onAmountChange = props.onAmountChange;
  var showThird = props.showThird;

  var val = (compEntry && typeof compEntry === 'object')
    ? Number(compEntry.amount || 0)
    : (compEntry || comp.defaultAmount);
  var toBePaid = (compEntry && typeof compEntry === 'object')
    ? Number(compEntry.toBePaid || 0)
    : 0;
  var isDeduction = toBePaid < 0;

  return (
    <div className="pr-comp-row">
      <div className="pr-comp-name">{comp.label}</div>
      <input
        className="pr-input pr-comp-rate"
        type="number"
        value={val}
        disabled={!isCurrentMonth}
        readOnly={isReadOnly}
        onChange={function(e) { onAmountChange(comp.id, Number(e.target.value || 0)); }}
        style={{ width: 90 }}
      />
      {showThird && (
        <>
          <div className="pr-comp-mult">{'\u00D7'} {backendValue}</div>
          <div className={'pr-comp-result' + (isDeduction ? ' ded' : toBePaid > 0 ? ' earn' : '')}>
            {isDeduction ? '-' : ''}{'\u20B9'}{Math.round(Math.abs(toBePaid)).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
}

function CardSubtotal(props) {
  var label = props.label;
  var amount = props.amount;
  var isDeduction = amount < 0;
  return (
    <div className="pr-card-subtotal">
      <span style={{ color: '#636e72' }}>{label}</span>
      <span style={{ color: isDeduction ? 'var(--pr-coral)' : 'var(--pr-teal)', fontWeight: 800 }}>
        {isDeduction ? '-' : ''}{'\u20B9'}{Math.round(Math.abs(amount)).toLocaleString()}
      </span>
    </div>
  );
}

export default function SalaryTab(props) {
  var selectedUser = props.selectedUser;
  var selectedMonth = props.selectedMonth;
  var salaryState = props.salaryState;
  var workingDaysOfMonth = props.workingDaysOfMonth;
  var showThirdColumn = props.showThirdColumn;
  var loading = props.loading;
  var saving = props.saving;
  var sampleData = props.sampleData;
  var isCurrentMonth = props.isCurrentMonth;
  var getBonusForMonth = props.getBonusForMonth;
  var sumComponentsToBePaid = props.sumComponentsToBePaid;
  var sumBonusesToBePaid = props.sumBonusesToBePaid;
  var getBackendValue = props.getBackendValue;
  var updateBonusField = props.updateBonusField;
  var updateComponentValue = props.updateComponentValue;
  var generateSalary = props.generateSalary;
  var saveSalaryStructure = props.saveSalaryStructure;
  var savePayroll = props.savePayroll;
  var printSalarySlip = props.printSalarySlip;
  var prevMonth = props.prevMonth;
  var nextMonth = props.nextMonth;
  var payrollStatusForMonth = props.payrollStatusForMonth;
  var setSalaryState = props.setSalaryState;

  if (!selectedUser) return null;

  var baseSalary = Number((salaryState && salaryState.baseSalary) || 0);
  var perDay = (baseSalary / 26).toFixed(2);
  var bonusTotal = sumBonusesToBePaid(salaryState && salaryState.bonuses);
  var compTotal = sumComponentsToBePaid(salaryState && salaryState.components);
  var netPay = (salaryState && salaryState.totalSalary) || Math.round(baseSalary + bonusTotal + compTotal);
  var isCurrent = isCurrentMonth(selectedMonth);

  // Helper to get component entry + backend value
  function getComp(id) {
    var compEntry = salaryState && salaryState.components && salaryState.components[id];
    var sd = sampleData.find(function(s) { return s.id === id; });
    var backendValue = getBackendValue(salaryState && salaryState.backendData, sd && sd.backendField);
    return { compEntry: compEntry, sd: sd, backendValue: backendValue };
  }

  // Sum toBePaid for a group of component IDs
  function groupTotal(ids) {
    var sum = 0;
    ids.forEach(function(id) {
      var c = getComp(id);
      if (c.compEntry && typeof c.compEntry === 'object') sum += Number(c.compEntry.toBePaid || 0);
    });
    return sum;
  }

  // Earnings subtotal (components > 0)
  var earningsTotal = 0;
  var deductionsTotal = 0;
  if (salaryState && salaryState.components) {
    Object.values(salaryState.components).forEach(function(c) {
      var tb = Number((c && c.toBePaid) || 0);
      if (tb >= 0) earningsTotal += tb;
      else deductionsTotal += tb;
    });
  }
  var totalPositive = baseSalary + earningsTotal + bonusTotal;
  var proportionEarn = totalPositive > 0 ? Math.round((totalPositive / (totalPositive + Math.abs(deductionsTotal))) * 100) : 100;

  return (
    <div>
      {/* Month Navigator */}
      <div className="pr-month-nav">
        <button className="pr-month-btn" onClick={prevMonth}>&larr;</button>
        <span className="pr-month-label">{formatMonth(selectedMonth)}</span>
        <button className="pr-month-btn" onClick={nextMonth}>&rarr;</button>
      </div>

      {/* KPI Cards */}
      <div className="pr-kpi-row">
        <div className="pr-kpi-card">
          <div className="pr-kpi-card-label">Base Salary</div>
          <div className="pr-kpi-card-value">{'\u20B9'}{baseSalary.toLocaleString()}</div>
        </div>
        <div className="pr-kpi-card">
          <div className="pr-kpi-card-label">Bonuses</div>
          <div className="pr-kpi-card-value">{'\u20B9'}{Math.round(bonusTotal).toLocaleString()}</div>
        </div>
        <div className="pr-kpi-card">
          <div className="pr-kpi-card-label">Working Days</div>
          <div className="pr-kpi-card-value">{workingDaysOfMonth}</div>
        </div>
        <div className="pr-kpi-card highlight">
          <div className="pr-kpi-card-label">Net Pay</div>
          <div className="pr-kpi-card-value">{'\u20B9'}{netPay.toLocaleString()}</div>
        </div>
      </div>

      {/* Status messages */}
      {payrollStatusForMonth() === 'beforeJoining' && (
        <div className="pr-card" style={{ textAlign: 'center', color: '#E17055' }}>
          Cannot generate payroll for months before joining date.
        </div>
      )}
      {payrollStatusForMonth() === 'exists' && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <span className="pr-status-badge generated">Payroll Exists</span>
        </div>
      )}

      {loading && <div className="pr-loading">Loading / calculating...</div>}

      {/* ── Base Salary Card ── */}
      <div className="pr-earn-card violet-border">
        <div className="pr-card-header">Base Salary</div>
        <div className="pr-comp-row">
          <div className="pr-comp-name" style={{ fontWeight: 700 }}>Monthly Base</div>
          <input
            className="pr-input"
            type="number"
            value={baseSalary}
            disabled={!isCurrent}
            onChange={function(e) {
              setSalaryState(function(prev) { return Object.assign({}, prev, { baseSalary: Number(e.target.value || 0) }); });
            }}
            style={{ width: 120 }}
          />
          {showThirdColumn && (
            <>
              <div className="pr-comp-mult">26 days</div>
              <div className="pr-comp-result earn">{'\u20B9'}{baseSalary.toLocaleString()}</div>
            </>
          )}
        </div>
        <div className="pr-comp-row">
          <div className="pr-comp-name" style={{ color: '#636e72', fontSize: '0.82rem' }}>Per Day Rate ({'\u00F7'} 26)</div>
          <input className="pr-input" type="number" value={perDay} readOnly style={{ width: 120 }} />
          {showThirdColumn && (
            <>
              <div className="pr-comp-mult"></div>
              <div className="pr-comp-result" style={{ fontSize: '0.78rem', color: '#636e72' }}>auto-used below</div>
            </>
          )}
        </div>
      </div>

      {/* ── Reviews & Content Card ── */}
      <div className="pr-earn-card">
        <div className="pr-card-header">Reviews & Content</div>
        {REVIEWS_IDS.map(function(id) {
          var c = getComp(id);
          if (!c.sd) return null;
          return (
            <CompRow key={id} comp={c.sd} compEntry={c.compEntry} backendValue={c.backendValue}
              isReadOnly={c.sd.markReadOnly} isCurrentMonth={isCurrent}
              onAmountChange={updateComponentValue} showThird={showThirdColumn} />
          );
        })}
        {showThirdColumn && <CardSubtotal label="Subtotal" amount={groupTotal(REVIEWS_IDS)} />}
      </div>

      {/* ── Patient Incentives Card ── */}
      <div className="pr-earn-card">
        <div className="pr-card-header">Patient Incentives</div>
        {PATIENT_IDS.map(function(id) {
          var c = getComp(id);
          if (!c.sd) return null;
          return (
            <CompRow key={id} comp={c.sd} compEntry={c.compEntry} backendValue={c.backendValue}
              isReadOnly={c.sd.markReadOnly} isCurrentMonth={isCurrent}
              onAmountChange={updateComponentValue} showThird={showThirdColumn} />
          );
        })}
        {showThirdColumn && <CardSubtotal label="Subtotal" amount={groupTotal(PATIENT_IDS)} />}
      </div>

      {/* ── Checklist Bonus Card ── */}
      {(function() {
        var c = getComp(CHECKLIST_ID);
        if (!c.sd) return null;
        var compEntry = c.compEntry;
        var toBePaid = (compEntry && typeof compEntry === 'object') ? Number(compEntry.toBePaid || 0) : 0;
        var passed = toBePaid > 0;
        return (
          <div className="pr-earn-card">
            <div className="pr-card-header" style={{ display: '-webkit-flex', display: 'flex', WebkitAlignItems: 'center', alignItems: 'center', WebkitJustifyContent: 'space-between', justifyContent: 'space-between' }}>
              <span>Checklist Bonus</span>
              {showThirdColumn && (
                <span className={'pr-pass-badge ' + (passed ? 'pass' : 'fail')}>
                  {passed ? 'PASS' : 'FAIL'}
                </span>
              )}
            </div>
            <CompRow comp={c.sd} compEntry={c.compEntry} backendValue={c.backendValue}
              isReadOnly={c.sd.markReadOnly} isCurrentMonth={isCurrent}
              onAmountChange={updateComponentValue} showThird={showThirdColumn} />
            {showThirdColumn && (
              <div style={{ fontSize: '0.78rem', color: '#636e72', marginTop: 6 }}>
                {passed ? 'All checklists completed — full bonus earned' : 'Incomplete checklists — bonus forfeited'}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Overtime & Extra Card ── */}
      <div className="pr-earn-card">
        <div className="pr-card-header">Overtime & Extra</div>
        {OVERTIME_IDS.map(function(id) {
          var c = getComp(id);
          if (!c.sd) return null;
          return (
            <CompRow key={id} comp={c.sd} compEntry={c.compEntry} backendValue={c.backendValue}
              isReadOnly={c.sd.markReadOnly} isCurrentMonth={isCurrent}
              onAmountChange={updateComponentValue} showThird={showThirdColumn} />
          );
        })}
        {showThirdColumn && <CardSubtotal label="Subtotal" amount={groupTotal(OVERTIME_IDS)} />}
      </div>

      {/* ── Deductions Card ── */}
      <div className="pr-ded-card">
        <div className="pr-card-header" style={{ color: 'var(--pr-coral)' }}>Deductions</div>
        {[OTT_ID, ABSENT_ID].map(function(id) {
          var c = getComp(id);
          if (!c.sd) return null;
          return (
            <CompRow key={id} comp={c.sd} compEntry={c.compEntry} backendValue={c.backendValue}
              isReadOnly={c.sd.markReadOnly} isCurrentMonth={isCurrent}
              onAmountChange={updateComponentValue} showThird={showThirdColumn} />
          );
        })}
        {showThirdColumn && <CardSubtotal label="Total Deductions" amount={groupTotal([OTT_ID, ABSENT_ID])} />}
      </div>

      {/* ── Bonuses Card ── */}
      <div className="pr-card">
        <div className="pr-card-header">Bonuses</div>
        <div className="pr-bonus-grid">
          {['joiningBonus', 'retentionBonus', 'referralBonus', 'specialAllowance'].map(function(bk) {
            var bobj = (salaryState && salaryState.bonuses && salaryState.bonuses[bk])
              ? salaryState.bonuses[bk] : { amount: 0, mode: 'one-time', toBePaid: 0 };
            var toBePaidShown = Math.round(getBonusForMonth(bobj));
            return (
              <div className="pr-bonus-grid-item" key={bk}>
                <div className="pr-bonus-mini">
                  <div className="pr-bonus-mini-label">{BONUS_LABELS[bk]}</div>
                  <input
                    className="pr-input"
                    type="number"
                    value={bobj.amount || 0}
                    disabled={!isCurrent}
                    onChange={function(e) { updateBonusField(bk, 'amount', Number(e.target.value || 0)); }}
                    style={{ width: '100%', marginBottom: 6 }}
                  />
                  <select
                    className="pr-select"
                    value={bobj.mode || 'one-time'}
                    disabled={!isCurrent}
                    onChange={function(e) { updateBonusField(bk, 'mode', e.target.value); }}
                    style={{ width: '100%', marginBottom: 6 }}
                  >
                    <option value="one-time">One-time</option>
                    <option value="monthly">Monthly ({'\u00F7'}12)</option>
                    <option value="quarterly">Quarterly ({'\u00F7'}4)</option>
                    <option value="bi-annual">Bi-Annual ({'\u00F7'}2)</option>
                    <option value="annual">Annual</option>
                  </select>
                  <div style={{ textAlign: 'right' }}>
                    <span className="pr-bonus-payable-chip">{'\u20B9'}{toBePaidShown.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Summary Card ── */}
      <div className="pr-summary">
        <div className="pr-summary-row">
          <span className="pr-summary-label">Base Salary</span>
          <span className="pr-summary-value">{'\u20B9'}{baseSalary.toLocaleString()}</span>
        </div>
        <div className="pr-summary-row">
          <span className="pr-summary-label">Earnings (Components)</span>
          <span className="pr-summary-value" style={{ color: 'var(--pr-teal)' }}>+{'\u20B9'}{Math.round(earningsTotal).toLocaleString()}</span>
        </div>
        <div className="pr-summary-row">
          <span className="pr-summary-label">Deductions</span>
          <span className="pr-summary-value" style={{ color: 'var(--pr-coral)' }}>{'\u20B9'}{Math.round(deductionsTotal).toLocaleString()}</span>
        </div>
        <div className="pr-summary-row">
          <span className="pr-summary-label">Bonuses</span>
          <span className="pr-summary-value">{'\u20B9'}{Math.round(bonusTotal).toLocaleString()}</span>
        </div>

        {/* Proportion bar */}
        <div className="pr-proportion-bar">
          <div className="pr-proportion-earn" style={{ width: proportionEarn + '%' }} />
          <div className="pr-proportion-ded" style={{ width: (100 - proportionEarn) + '%' }} />
        </div>

        <div className="pr-summary-row total">
          <span className="pr-summary-label">Net Payable</span>
          <span className="pr-summary-value">{'\u20B9'}{netPay.toLocaleString()}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pr-actions">
        <button className="pr-btn teal" onClick={saveSalaryStructure} disabled={loading}>
          Save Structure
        </button>
        <button className="pr-btn blue" onClick={generateSalary} disabled={loading}>
          {payrollStatusForMonth() === 'exists' ? 'Regenerate' : 'Generate'}
        </button>
        <button className="pr-btn violet" onClick={savePayroll} disabled={saving || loading || !showThirdColumn}>
          {saving ? 'Saving...' : 'Save Payroll'}
        </button>
        <button className="pr-btn coral" onClick={printSalarySlip} disabled={!showThirdColumn}>
          Print Slip
        </button>
      </div>
    </div>
  );
}
